import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  InputField,
  Select,
  SubmitButton,
} from '@saas-kit/ui';

import type { QmsCategory } from './types';

export interface ServiceCreateInput {
  code: string;
  name: string;
  name_ar: string | null;
  category_id: string | null;
  token_prefix: string | null;
  daily_cap: number | null;
}

export function ServiceCreateForm({
  categories,
  creating,
  onCreate,
}: {
  categories: QmsCategory[];
  creating: boolean;
  onCreate: (input: ServiceCreateInput) => Promise<unknown>;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [prefix, setPrefix] = useState('');
  const [dailyCap, setDailyCap] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    await onCreate({
      code: code.trim(),
      name: name.trim(),
      name_ar: nameAr.trim() || null,
      category_id: categoryId || null,
      token_prefix: prefix.trim() || null,
      daily_cap: dailyCap ? Number(dailyCap) : null,
    });
    setCode('');
    setName('');
    setNameAr('');
    setCategoryId('');
    setPrefix('');
    setDailyCap('');
  };

  return (
    <Card>
      <CardContent className="p-5">
        <form onSubmit={(e) => void submit(e)} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <InputField
              label={t('fields.code')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t('placeholders.service_code')}
              required
            />
            <InputField
              label={t('fields.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('placeholders.service_name')}
              required
            />
            <label className="block space-y-1 text-sm">
              <span className="text-foreground">{t('fields.category')}</span>
              <Select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">{t('fields.none')}</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </label>
          </div>
          <InputField
            label={t('fields.name_ar')}
            value={nameAr}
            onChange={(e) => setNameAr(e.target.value)}
            placeholder={t('placeholders.service_name_ar')}
            dir="rtl"
            lang="ar"
          />
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <InputField
              label={t('fields.token_prefix')}
              value={prefix}
              maxLength={8}
              onChange={(e) => setPrefix(e.target.value)}
              placeholder={t('placeholders.service_token_prefix')}
            />
            <InputField
              label={t('fields.daily_cap')}
              type="number"
              min={1}
              value={dailyCap}
              onChange={(e) => setDailyCap(e.target.value)}
              placeholder={t('placeholders.service_daily_cap')}
            />
            <SubmitButton
              loading={creating}
              label={t('actions.create')}
              loadingLabel={t('actions.create')}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
